import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { PHONE_HREF } from "@/data/site";
import { scrollTo } from "./Reveal";

const LINKS = [
  { label: "Início", hash: "#inicio", id: "nav-link-inicio" },
  { label: "Serviços", hash: "#servicos", id: "nav-link-servicos" },
  { label: "Sobre Nós", hash: "#sobre", id: "nav-link-sobre" },
  { label: "Galeria", hash: "#galeria", id: "nav-link-galeria" },
  { label: "Contato", hash: "#contato", id: "nav-link-contato" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (hash) => {
    setOpen(false);
    scrollTo(hash);
  };

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-rose-100/70 shadow-[0_4px_30px_rgba(194,24,91,0.06)]"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 h-[72px] flex items-center justify-between">
        <button
          data-testid="navbar-logo"
          onClick={() => go("#inicio")}
          className="flex items-center gap-3 group"
        >
          <span className="w-11 h-11 rounded-full bg-[#C2185B] text-white font-serif-display italic text-xl flex items-center justify-center shadow-lg shadow-[#C2185B]/25 group-hover:scale-105 transition-transform duration-300">
            EB
          </span>
          <span className="text-left leading-tight">
            <span className="block font-serif-display text-lg font-semibold tracking-tight text-stone-900">
              Espaço da Beleza
            </span>
            <span className="block text-[10px] tracking-[0.28em] uppercase text-[#C2185B] font-medium">
              Estética &amp; Unhas
            </span>
          </span>
        </button>

        <ul className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <li key={l.hash}>
              <button
                data-testid={l.id}
                onClick={() => go(l.hash)}
                className="link-underline text-sm font-medium text-stone-700 hover:text-[#C2185B] transition-colors duration-300"
              >
                {l.label}
              </button>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-3">
          <a
            data-testid="nav-cta-call"
            href={PHONE_HREF}
            className="hidden sm:inline-flex items-center gap-2 bg-[#C2185B] hover:bg-[#A2134B] text-white text-sm font-semibold px-6 py-3 rounded-full transition-all duration-300 hover:shadow-xl hover:shadow-[#C2185B]/30 hover:-translate-y-0.5"
          >
            <Phone size={15} strokeWidth={2.5} />
            Ligar e Agendar
          </a>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden w-11 h-11 rounded-full border border-rose-200 flex items-center justify-center text-stone-800 bg-white/70"
            aria-label="Menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: "easeInOut" }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-b border-rose-100"
          >
            <ul className="px-6 py-6 space-y-1">
              {LINKS.map((l) => (
                <li key={l.hash}>
                  <button
                    onClick={() => go(l.hash)}
                    className="w-full text-left font-serif-display text-2xl py-2.5 text-stone-800 hover:text-[#C2185B] transition-colors"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
              <li className="pt-3">
                <a
                  href={PHONE_HREF}
                  className="inline-flex items-center gap-2 bg-[#C2185B] text-white font-semibold px-6 py-3.5 rounded-full w-full justify-center"
                >
                  <Phone size={16} /> Ligar e Agendar — 92473544
                </a>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
